import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, FolderPlus, Folder, FolderOpen, Upload, Search, X,
  Trash2, ChevronRight, ChevronDown, Pencil, MoveRight, Image as ImageIcon,
  CheckSquare, Square,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import FileDropZone, { UploadProgress } from "@/components/admin/FileDropZone";
import { safeReadBody } from "@/lib/fetchWithRetry";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || "";

// Admin Media Library uses Bunny for image delivery whenever the
// pull-zone variable is set. We pick WebP for thumbnails (240 px) and
// the preview modal (1280 px) because the picker grid hits Bunny with
// dozens of cold-cache transforms when a folder opens — AVIF encoding
// at scale hits Bunny Optimizer's per-zone budget. WebP at this size
// is visually indistinguishable and 5-10× faster to encode.
const ADMIN_THUMB_WIDTH = 240;
const ADMIN_PREVIEW_WIDTH = 1280;

const buildCdnImage = (rawUrl, width, format = "webp", quality = 72) => {
  const path = (rawUrl || "").split("?")[0];
  return `${CDN_BASE_URL}${path}?width=${width}&format=${format}&quality=${quality}`;
};

const resolveUrl = (u) => {
  if (!u) return "";
  if (!u.startsWith("/api/")) return u;
  if (CDN_BASE_URL) {
    return buildCdnImage(u, ADMIN_THUMB_WIDTH);
  }
  return `${API}${u}`;
};

const resolvePreviewUrl = (u) => {
  if (!u) return "";
  if (!u.startsWith("/api/")) return u;
  if (CDN_BASE_URL) {
    return buildCdnImage(u, ADMIN_PREVIEW_WIDTH);
  }
  return `${API}${u}`;
};

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────
const parentOf = (path) => {
  if (!path || path === "/") return "/";
  const parts = path.split("/").filter(Boolean);
  parts.pop();
  return parts.length ? "/" + parts.join("/") : "/";
};

const buildTree = (folders) => {
  // returns a map: parent → children[]
  const byParent = new Map();
  byParent.set("/", []);
  folders.forEach((f) => {
    if (!byParent.has(f.parent)) byParent.set(f.parent, []);
    byParent.get(f.parent).push(f);
    if (!byParent.has(f.path)) byParent.set(f.path, []);
  });
  return byParent;
};

// Flatten the folder tree depth-first into a list of [folder, depth] entries
// (skipping subtrees whose ancestor is collapsed). Used to render the sidebar
// without recursive JSX so Babel can parse it cleanly.
const flattenFolders = (byParent, openSet, parent = "/", depth = 1) => {
  const out = [];
  const kids = byParent.get(parent) || [];
  for (const k of kids) {
    out.push({ folder: k, depth });
    if (openSet.has(k.path)) {
      const child = flattenFolders(byParent, openSet, k.path, depth + 1);
      for (const c of child) out.push(c);
    }
  }
  return out;
};

// ────────────────────────────────────────────────────────────────────────
// Folder row (flat-list item)
// ────────────────────────────────────────────────────────────────────────
const FolderRow = ({ folder, depth, hasChildren, isOpen, isActive, onToggle, onSelect, onRename, onDelete }) => {
  return (
    <div
      className={"flex items-center gap-1.5 pr-2 py-1.5 cursor-pointer transition-colors group " + (isActive ? "bg-[#2C2621] text-[#FDFBF7]" : "hover:bg-[#F2EBE1]/60 text-[#2C2621]")}
      style={{ paddingLeft: depth * 14 + 8 }}
      onClick={() => onSelect(folder.path)}
      data-testid={`folder-row-${folder.path}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(folder.path); }}
        className="shrink-0"
        aria-label={isOpen ? "Collapse" : "Expand"}
      >
        {hasChildren ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="inline-block w-3.5" />}
      </button>
      {isOpen ? <FolderOpen className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} /> : <Folder className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />}
      <span className="text-sm truncate flex-1">{folder.name}</span>
      <span className={"hidden group-hover:flex items-center gap-1 shrink-0 " + (isActive ? "text-[#FDFBF7]" : "text-[#5C5248]")}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRename(folder); }}
          className="hover:text-[#C16542]"
          aria-label="Rename"
          data-testid={`folder-rename-${folder.path}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(folder); }}
          className="hover:text-[#C16542]"
          aria-label="Delete"
          data-testid={`folder-delete-${folder.path}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </span>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Asset preview modal
// ────────────────────────────────────────────────────────────────────────
const AssetPreview = ({ asset, onClose, onSaved, onDelete, folders }) => {
  const [filename, setFilename] = useState(asset.filename || "");
  const [title, setTitle] = useState(asset.title || "");
  const [folder, setFolder] = useState(asset.folder || "/");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/media/assets/${asset.id}`, {
      credentials: "include",
        method: "PATCH",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ filename, title, folder }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const updated = await res.json();
      onSaved(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${asset.filename}"? Existing references on the site will continue to load, but it will disappear from the library.`)) return;
    const res = await fetch(`${API}/api/admin/media/assets/${asset.id}`, {
      credentials: "include", method: "DELETE", headers: adminHeaders() });
    if (res.ok) onDelete(asset.id);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] bg-[#1A1513]/85 flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="asset-preview-modal"
    >
      <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 border-b border-[#2C2621]/10">
          <p className="overline text-[#C16542]">Media Asset</p>
          <button type="button" onClick={onClose} className="text-[#5C5248] hover:text-[#C16542]" aria-label="Close" data-testid="asset-preview-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-[#F2EBE1] flex items-center justify-center min-h-[300px] max-h-[80vh] overflow-hidden">
            <img src={resolvePreviewUrl(asset.url)} alt={asset.title || asset.filename} className="max-w-full max-h-[80vh] object-contain" loading="lazy" decoding="async"/>
          </div>
          <div className="p-6 md:p-8 space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Filename</label>
              <input value={filename} onChange={(e) => setFilename(e.target.value)} className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white" data-testid="asset-preview-filename" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Hotel lobby — evening" className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white" data-testid="asset-preview-title" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Folder</label>
              <select value={folder} onChange={(e) => setFolder(e.target.value)} className="mt-2 w-full border border-[#2C2621]/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#C16542] bg-white" data-testid="asset-preview-folder">
                <option value="/">/ (root)</option>
                {folders.map((f) => <option key={f.path} value={f.path}>{f.path}</option>)}
              </select>
            </div>
            <div className="pt-1 text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/80">
              {(asset.content_type || "image").split("/").pop()} · {Math.round((asset.size || 0)/1024)} KB
            </div>
            <div className="pt-2 break-all text-[11px] text-[#5C5248] font-mono">{asset.url}</div>
            {error && <p className="text-sm text-[#C16542]">{error}</p>}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-[#2C2621]/10">
              <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-5 py-3 text-[11px] tracking-[0.25em] uppercase" data-testid="asset-preview-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoveRight className="w-4 h-4" />}
                Save
              </button>
              <button type="button" onClick={remove} className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-5 py-3 text-[11px] tracking-[0.25em] uppercase" data-testid="asset-preview-delete">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────
const AdminMediaInner = () => {
  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [currentPath, setCurrentPath] = useState("/");
  const [openSet, setOpenSet] = useState(() => new Set(["/"]));
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const byParent = useMemo(() => buildTree(folders), [folders]);

  const loadFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/media/folders`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFolders(data.folders || []);
    } catch (err) { setError(err.message); }
  }, []);

  const loadAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const params = new URLSearchParams();
      if (filter.trim()) params.set("q", filter.trim());
      else params.set("folder", currentPath);
      const res = await fetch(`${API}/api/admin/media/assets?${params}`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const items = data.items || [];
      setAssets(items);
      // Prune any selection that no longer maps to a visible asset — keeps
      // the floating action bar from sticking around after a refresh.
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev;
        const visible = new Set(items.map((a) => a.id));
        const next = new Set();
        for (const id of prev) if (visible.has(id)) next.add(id);
        return next.size === prev.size ? prev : next;
      });
    } catch (err) { setError(err.message); }
    finally { setLoadingAssets(false); }
  }, [currentPath, filter]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  const goToFolder = useCallback((p) => {
    setCurrentPath(p);
    setSelectedIds(new Set());
  }, []);

  const updateFilter = useCallback((value) => {
    setFilter(value);
    setSelectedIds(new Set());
  }, []);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(assets.map((a) => a.id)));
  }, [assets]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = window.confirm(
      `Delete ${ids.length} image${ids.length > 1 ? "s" : ""} from the Media Library? ` +
      `This removes them from the library. Existing references already published on the site will keep loading.`
    );
    if (!ok) return;
    setBulkDeleting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/media/assets/bulk-delete`, {
      credentials: "include",
        method: "POST",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const msg = await safeReadBody(res);
        throw new Error(msg ? `Bulk delete failed: ${msg.slice(0, 200)}` : `Bulk delete failed (HTTP ${res.status})`);
      }
      setSelectedIds(new Set());
      await loadAssets();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, loadAssets]);

  const toggleFolder = (path) => {
    setOpenSet((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const onNewFolder = async () => {
    const name = window.prompt("New folder name (in " + currentPath + "):");
    if (!name) return;
    setError("");
    const res = await fetch(`${API}/api/admin/media/folders`, {
      credentials: "include",
      method: "POST",
      headers: { ...adminHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ parent: currentPath, name }),
    });
    if (!res.ok) { setError(`Create folder failed (${res.status})`); return; }
    await loadFolders();
    setOpenSet((s) => new Set([...s, currentPath]));
  };

  const onRenameFolder = async (folder) => {
    const v = window.prompt("Rename folder " + folder.name + " to:", folder.name);
    if (!v || v === folder.name) return;
    const url = `${API}/api/admin/media/folders/rename?path=${encodeURIComponent(folder.path)}`;
    const res = await fetch(url, {
      credentials: "include",
      method: "PUT",
      headers: { ...adminHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ new_name: v }),
    });
    if (!res.ok) { setError(`Rename failed (${res.status})`); return; }
    const data = await res.json();
    if (currentPath === folder.path) setCurrentPath(data.new);
    if (currentPath.startsWith(folder.path + "/")) setCurrentPath(data.new + currentPath.slice(folder.path.length));
    await loadFolders();
    await loadAssets();
  };

  const onDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder ${folder.path}? It must be empty first.`)) return;
    const url = `${API}/api/admin/media/folders?path=${encodeURIComponent(folder.path)}`;
    const res = await fetch(url, {
      credentials: "include", method: "DELETE", headers: adminHeaders() });
    if (!res.ok) {
      const msg = await safeReadBody(res);
      setError(msg ? msg.slice(0, 200) : `Delete failed (HTTP ${res.status})`);
      return;
    }
    if (currentPath === folder.path || currentPath.startsWith(folder.path + "/")) {
      setCurrentPath(parentOf(folder.path));
    }
    await loadFolders();
  };

  const onUploadFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    setError("");
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", currentPath);
        const res = await fetch(`${API}/api/admin/media/assets/upload`, {
      credentials: "include",
          method: "POST", headers: adminHeaders(), body: fd,
        });
        if (!res.ok) {
          const msg = await safeReadBody(res);
          throw new Error(msg ? `Upload failed: ${msg.slice(0, 120)}` : `Upload failed (HTTP ${res.status})`);
        }
        setUploadProgress({ done: i + 1, total: files.length });
      }
      await loadAssets();
    } catch (err) { setError(err.message); }
    finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  }, [currentPath, loadAssets]);

  const onUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    onUploadFiles(files);
  }, [onUploadFiles]);

  // Breadcrumb
  const crumbs = useMemo(() => {
    if (currentPath === "/") return [{ label: "/", path: "/" }];
    const parts = currentPath.split("/").filter(Boolean);
    const out = [{ label: "/", path: "/" }];
    let acc = "";
    for (const p of parts) {
      acc += "/" + p;
      out.push({ label: p, path: acc });
    }
    return out;
  }, [currentPath]);

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-media-page">
      <header className="px-6 md:px-10 py-7 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/admin" className="overline text-[#C16542] flex items-center gap-1.5 hover:underline" data-testid="admin-media-back">
              <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Admin · Media Library
            </Link>
            <h1 className="font-serif-x text-4xl md:text-5xl mt-2 tracking-tight">Media Library</h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Central repository for every image used on the website. Upload, organise in folders, rename, move, search.
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row min-h-[calc(100vh-150px)]">
        {/* SIDEBAR */}
        <aside className="lg:w-[320px] xl:w-[360px] border-b lg:border-b-0 lg:border-r border-[#2C2621]/10 bg-white" data-testid="admin-media-sidebar">
          <div className="px-4 py-4 border-b border-[#2C2621]/10 flex items-center justify-between gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">Folders</span>
            <button
              type="button"
              onClick={onNewFolder}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#C16542] hover:underline"
              data-testid="new-folder-btn"
            >
              <FolderPlus className="w-3.5 h-3.5" /> New
            </button>
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto py-2">
            <div
              className={`flex items-center gap-2 pl-2 pr-2 py-1.5 cursor-pointer ${currentPath === "/" ? "bg-[#2C2621] text-[#FDFBF7]" : "text-[#2C2621] hover:bg-[#F2EBE1]/60"}`}
              onClick={() => goToFolder("/")}
              data-testid="folder-row-/"
            >
              <span className="w-3.5" />
              <Folder className="w-3.5 h-3.5" />
              <span className="text-sm">/ (root)</span>
            </div>
            {(byParent.get("/") || []).length === 0 && folders.length === 0 ? (
              <p className="px-4 py-3 text-xs text-[#5C5248] italic">No folders yet — create one above.</p>
            ) : flattenFolders(byParent, openSet).map(({ folder, depth }) => (
              <FolderRow
                key={folder.path}
                folder={folder}
                depth={depth}
                hasChildren={(byParent.get(folder.path) || []).length > 0}
                isOpen={openSet.has(folder.path)}
                isActive={currentPath === folder.path}
                onToggle={toggleFolder}
                onSelect={goToFolder}
                onRename={onRenameFolder}
                onDelete={onDeleteFolder}
              />
            ))}
          </div>
        </aside>

        {/* MAIN */}
        <FileDropZone
          onFiles={onUploadFiles}
          disabled={uploading}
          className="flex-1 min-w-0"
          overlayLabel="Drop images to add to this folder"
          testid="media-dropzone"
        >
          {/* TOOLBAR */}
          <div className="px-6 md:px-8 py-5 border-b border-[#2C2621]/10 bg-white flex flex-wrap items-center gap-3">
            <nav className="flex items-center gap-1 text-sm" data-testid="media-breadcrumbs">
              {crumbs.map((c, i) => (
                <span key={c.path} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => goToFolder(c.path)}
                    className={`hover:text-[#C16542] ${currentPath === c.path ? "text-[#2C2621]" : "text-[#5C5248]"}`}
                  >
                    {c.label}
                  </button>
                  {i < crumbs.length - 1 && <span className="text-[#5C5248]/50">/</span>}
                </span>
              ))}
            </nav>
            <div className="flex-1" />
            {uploading && (
              <UploadProgress done={uploadProgress.done} total={uploadProgress.total} />
            )}
            <label className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5248]" strokeWidth={1.6} />
              <input
                type="search"
                placeholder="Search across all folders…"
                value={filter}
                onChange={(e) => updateFilter(e.target.value)}
                className="w-72 max-w-full pl-9 pr-3 py-2 text-sm border border-[#2C2621]/15 focus:outline-none focus:border-[#C16542]"
                data-testid="media-search"
              />
            </label>
            <label className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase cursor-pointer transition-colors" data-testid="media-upload-trigger">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.6} />}
              {uploading ? "Uploading…" : "Upload"}
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
                className="hidden"
                onChange={onUpload}
                disabled={uploading}
                data-testid="media-upload-input"
              />
            </label>
          </div>

          {/* CONTENT */}
          <div className="px-6 md:px-8 py-8">
            {error && (
              <div className="mb-5 border border-[#C16542]/30 bg-[#C16542]/10 px-4 py-3 text-sm text-[#C16542] flex items-center gap-3" data-testid="media-error">
                {error}
                <button type="button" onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* Sub-folders */}
            {!filter && (byParent.get(currentPath) || []).length > 0 && (
              <>
                <p className="overline text-[#5C5248] mb-4">Folders</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-10">
                  {(byParent.get(currentPath) || []).map((f) => (
                    <button
                      key={f.path}
                      type="button"
                      onClick={() => goToFolder(f.path)}
                      className="flex items-center gap-3 px-4 py-4 border border-[#2C2621]/10 hover:border-[#C16542]/60 transition-colors bg-white"
                      data-testid={`folder-tile-${f.path}`}
                    >
                      <Folder className="w-5 h-5 text-[#A07042]" strokeWidth={1.6} />
                      <span className="text-sm truncate">{f.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="overline text-[#5C5248]">
                {filter ? `Search results · ${assets.length}` : `Images · ${assets.length}`}
              </p>
              {assets.length > 0 && (
                <div className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase">
                  <button
                    type="button"
                    onClick={selectedIds.size === assets.length ? clearSelection : selectAllVisible}
                    className="inline-flex items-center gap-1.5 text-[#5C5248] hover:text-[#C16542]"
                    data-testid="media-select-all-toggle"
                  >
                    {selectedIds.size === assets.length ? (
                      <><CheckSquare className="w-3.5 h-3.5" /> Deselect all</>
                    ) : (
                      <><Square className="w-3.5 h-3.5" /> Select all</>
                    )}
                  </button>
                </div>
              )}
            </div>
            {loadingAssets ? (
              <div className="flex items-center gap-2 text-[#5C5248]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : assets.length === 0 ? (
              <div className="border border-dashed border-[#2C2621]/20 py-20 text-center text-[#5C5248]" data-testid="media-empty">
                <ImageIcon className="w-8 h-8 mx-auto mb-3" strokeWidth={1.4} />
                <p className="font-serif-x text-xl text-[#2C2621]">No images here yet</p>
                <p className="mt-1 text-sm">Drag and drop images here, or click <strong>Upload</strong> to add the first image to this folder.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
                {assets.map((a) => {
                  const isSelected = selectedIds.has(a.id);
                  const hasSelection = selectedIds.size > 0;
                  return (
                    <div
                      key={a.id}
                      className={
                        "group relative block bg-white border overflow-hidden text-left transition-colors " +
                        (isSelected
                          ? "border-[#C16542] ring-2 ring-[#C16542]/30"
                          : "border-[#2C2621]/10 hover:border-[#C16542]/60")
                      }
                      data-testid={`media-asset-${a.id}`}
                      data-selected={isSelected ? "true" : "false"}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedAsset(a)}
                        className="block w-full text-left"
                        data-testid={`media-asset-open-${a.id}`}
                      >
                        <div className="aspect-square overflow-hidden bg-[#F2EBE1] relative">
                          <span aria-hidden="true" className="picker-thumb-skel absolute inset-0 pointer-events-none" />
                          <img
                            src={resolveUrl(a.url)}
                            alt={a.title || a.filename}
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                            onLoad={(e) => e.currentTarget.previousSibling && e.currentTarget.previousSibling.classList.add('picker-thumb-skel--done')}
                            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 relative z-[1]"
                          />
                        </div>
                        <div className="px-3 py-2.5">
                          <p className="text-sm truncate text-[#2C2621]" title={a.filename}>{a.title || a.filename}</p>
                          <p className="text-[10px] tracking-[0.18em] uppercase text-[#5C5248]/80 truncate">
                            {a.folder} · {Math.round((a.size || 0)/1024)} KB
                          </p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelected(a.id); }}
                        aria-label={isSelected ? "Deselect image" : "Select image"}
                        aria-pressed={isSelected}
                        className={
                          "absolute top-2 left-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-sm shadow-sm border transition-opacity " +
                          (isSelected
                            ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542] opacity-100"
                            : "bg-white/95 text-[#2C2621] border-[#2C2621]/20 " +
                              (hasSelection ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"))
                        }
                        data-testid={`media-asset-checkbox-${a.id}`}
                      >
                        {isSelected ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={1.8} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </FileDropZone>
      </div>

      {selectedAsset && (
        <AssetPreview
          asset={selectedAsset}
          folders={folders}
          onClose={() => setSelectedAsset(null)}
          onSaved={(u) => { setSelectedAsset(u); loadAssets(); }}
          onDelete={() => { setSelectedAsset(null); loadAssets(); }}
        />
      )}

      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] bg-[#2C2621] text-[#FDFBF7] shadow-xl border border-[#FDFBF7]/10 flex items-center gap-2 px-3 py-2 max-w-[95vw]"
          role="region"
          aria-label="Bulk selection actions"
          data-testid="media-bulk-action-bar"
        >
          <span className="overline text-[#FDFBF7]/70 px-2" data-testid="media-bulk-count">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={selectAllVisible}
            disabled={selectedIds.size === assets.length || assets.length === 0}
            className="text-[11px] tracking-[0.22em] uppercase px-3 py-2 hover:bg-[#FDFBF7]/10 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="media-bulk-select-all"
          >
            <CheckSquare className="w-3.5 h-3.5" /> Select all
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[11px] tracking-[0.22em] uppercase px-3 py-2 hover:bg-[#FDFBF7]/10 inline-flex items-center gap-1.5"
            data-testid="media-bulk-clear"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
          <button
            type="button"
            onClick={bulkDelete}
            disabled={bulkDeleting}
            className="text-[11px] tracking-[0.22em] uppercase px-4 py-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="media-bulk-delete"
          >
            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {bulkDeleting ? "Deleting…" : "Delete selected"}
          </button>
        </div>
      )}
    </section>
  );
};

const AdminMediaPage = () => (
  <AdminAuthGate>
    <AdminMediaInner />
  </AdminAuthGate>
);

export default AdminMediaPage;
